import { useEffect, useRef, useState } from "react";
import { Users, TrendingUp, BarChart3, Gauge, Droplets, Wallet, Fuel, Calendar, MoreVertical } from "lucide-react";
import Skeleton from '@mui/material/Skeleton';
import InnerLoader from "../../../../component/ui/loader/inner-loader";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { TableBuildingTag, TableCard } from "../../../../component/table/table-layout";
import { CustomDropdown } from "../../../../component/ui/dropdown/CustomDropdown";
import { CustomDropdownButton } from "../../../../component/ui/dropdown/CustomDropdownButton";
import { CustomDropdownMenu } from "../../../../component/ui/dropdown/CustomDropdownMenu";
import { CustomDropdownMenuList } from "../../../../component/ui/dropdown/CustomDropdownMenuList";
import { Card, CardContent, CardHeader, CardTitle, KPI, TitleHeading } from "../../../../component/ui/layout";
import { FeildWraper, FormCheckbox } from "../../../../component/form/form-layout";
import moment from "moment";
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import FilterCalendar from "../../../../component/ui/calendar/filter";
import { StatCardNew } from "../../../../component/new-dashboard/statsCard";
import { CostIcon, ReportIcon } from "../../../../component/icons/dashboard";
import AreaChart from "../../../../component/new-dashboard/AreaChart";
import LeadsStatusChart from "../../../../component/new-dashboard/reportChart";
import PostsStatusDonut from "../../../../component/new-dashboard/PostStatus";

export default function FuelOrgOverview() {
  const apiCall = useApiCall();
  const [dashboardData, setDashboardData] = useState({});
  const [isDashboardApiLoaded, setIsDashboardApiLoaded] = useState(false);
  const [isTrendLoad, setIsTrendLoad] = useState(false);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(moment(new Date()).format("YYYY-MM"));
  const [activeChart, setActiveChart] = useState("Cost");
  const [allTime, setAllTime] = useState(false);
  const [allTimeLoader, setAllTimeLoader] = useState(false);
  const [kpiData, setKpiData] = useState({});
  const [defaultMonthOption, setDefaultMonthOption] = useState("");
  const [monthlyTrendChart, setMonthlyTrendChart] = useState("Liters")

  const getDashboardData = async () => {
    setIsDashboardApiLoaded(false);
    if (allTime) {
      setAllTime(false)
    }
    try {
      const resp = await apiCall({ method: "get", url: `get-fuel-tracking-dashboard?month=${defaultMonthOption || selectedMonth}` });
      setDashboardData(resp);
      setKpiData(resp?.current_month)
      setAllTimeLoader(true)
      setIsDashboardApiLoaded(true);
    } catch (error) {
      console.warn("Error : ", error);
    }
  }

  const getMonthlyTrend = async () => {
    try {
      const resp = await apiCall({ method: "get", url: `get-monthly-fuel-trends` });
      setMonthlyTrend(resp.data);
      setIsTrendLoad(true);
    } catch (error) {
      console.log("Err", error)
    }
  }

  useEffect(() => {
    getMonthlyTrend();
  }, []);

  useEffect(() => {
    getDashboardData();
  }, [selectedMonth, defaultMonthOption])

  const monthlyChart = {
    chart: { type: "line", backgroundColor: "transparent", height: 400 },
    title: { text: "" },
    xAxis: {
      categories: monthlyTrend?.map((list) => list.month),
      lineColor: "#E5E7EB",
      labels: { style: { color: "#6B7280" } },
    },
    yAxis: {
      title: { text: "Monthly" },
      gridLineColor: "#F3F4F6",
      labels: { style: { color: "#6B7280" } },
    },
    legend: { align: "right", verticalAlign: "top" },
    series: [
      {
        name: "Liters",
        data: monthlyTrend?.map((d) => d.total_liters),
      },
      {
        name: "Cost",
        data: monthlyTrend?.map((d) => Number(d.total_cost?.replaceAll("PKR ", "")?.replaceAll(",", ""))),
      }
    ],
    credits: { enabled: false },
  };

  const serviceOptions = {
    chart: {
      type: "column",
      animation: {
        duration: 600,
        easing: "easeOutBounce"
      }
    },
    title: "none",
    credits: { enabled: false },
    xAxis: {
      categories: dashboardData?.location_breakdown?.map(item => item.location),
      title: "none"
    },
    yAxis: {
      min: 0,
      title: { text: activeChart }
    },
    tooltip: {
      shared: true,
    },
    plotOptions: {
      series: {
        grouping: true   // <— REQUIRED when you have 2 bars per category
      },
      column: {
        pointWidth: 45,
        pointPadding: 0.05,
        groupPadding: 0.2,
      }
    },
    series: activeChart == "Cost" ? [
      {
        name: "Amount",
        color: "#50E3C2",
        data: dashboardData?.location_breakdown?.map(item =>
          Number(item?.amount?.replaceAll("PKR ", "")?.replaceAll(",", ""))
        )
      }
    ] : [
      {
        name: "Liter",
        color: "#4A90E2",
        data: dashboardData?.location_breakdown?.map(item =>
          Number(item?.liters)
        )
      }
    ],
    legend: { enabled: false }, // single series, legend not needed
  };

  const changeAllTime = (e) => {
    setAllTimeLoader(false)
    setAllTime(e);
    setKpiData(e ? (dashboardData?.all_time) : (dashboardData?.current_month))

    setTimeout(() => {
      setAllTimeLoader(true)
    }, 250);
  }

  const inputRef = useRef()

  const openMonthPicker = () => {
    if (inputRef.current?.showPicker) {
      inputRef.current.showPicker()
    } else {
      inputRef.current?.focus()
      inputRef.current?.click()
    }
  }

  const chartData = {
    categories: monthlyTrend?.map((list) => list.month),
    data: monthlyTrendChart == "Liters" ? [
      {
        name: "Liters",
        color: "#3B82F6",
        data: monthlyTrend?.map((d) => d.total_liters),

        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "rgba(59,130,246,0.25)"], // top
            [1, "rgba(59,130,246,0.02)"], // bottom fade
          ],
        },
      },
    ] : [
      {
        name: "Cost",
        color: "#3B82F6",
        data: monthlyTrend?.map((d) => Number(d.total_cost?.replaceAll("PKR ", "")?.replaceAll(",", ""))),

        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "rgba(59,130,246,0.25)"], // top
            [1, "rgba(59,130,246,0.02)"], // bottom fade
          ],
        },
      },
    ]
  }

  // const secondChartData = {
  //   categories: dashboardData?.location_breakdown?.map(item =>
  //     item?.location
  //   ),
  //   data: activeChart == "Cost" ? dashboardData?.location_breakdown?.map(item =>
  //     (
  //       {
  //         y: parseInt(item?.amount?.replaceAll("PKR ", "")?.replaceAll(",", "")),
  //         color: "#0057FB",
  //         dataLabels: { enabled: false }
  //       }
  //     )
  //   ):
  //   dashboardData?.location_breakdown?.map(item =>
  //     (
  //       {
  //         y: parseInt(item?.liters?.replaceAll("PKR ", "")?.replaceAll(",", "")),
  //         color: "#0057FB",
  //         dataLabels: { enabled: false }
  //       }
  //     )
  //   )

  // }

  const secondChartData = activeChart == "Cost" ? dashboardData?.location_breakdown?.map((item , i) => (
    {
      name: item.location,
      y: parseInt(item?.amount?.replaceAll("PKR ", "").replaceAll(",", "") || 0),
      count: parseInt(item?.amount?.replaceAll("PKR ", "").replaceAll(",", "")) || 0,
      label: item?.amount,
      color: i == 0 ? "#FF4040" : "#0095FF"
    }
  ))
  :
  dashboardData?.location_breakdown?.map((item, i) => (
    {
      name: item.location,
      y: parseInt(item?.liters?.replaceAll("PKR ", "").replaceAll(",", "") || 0),
      count: parseInt(item?.liters?.replaceAll("PKR ", "").replaceAll(",", "")) || 0,
      label: item?.liters,
      color: i == 0 ? "#FF4040" : "#0095FF"
    }
  ))

  return (
    <>
      <div className="p-[10px] overflow-hidden h-[calc(100vh-58px)] overflow-y-auto">
        <div className="flex items-center justify-between">
          <TitleHeading>Fuel Overview</TitleHeading>
          <div className="flex gap-3 flex-shrink-0 w-fit items-center justify-end">
            <FeildWraper className={"!w-fit !flex-[0_0_auto] !pl-0 !py-0"}>
              <div className="relative">
                <input type="month" ref={inputRef} className="absolute opacity-0 pointer-events-none" max={moment(new Date()).format("YYYY-MM")} value={selectedMonth} onChange={(e) => { setDefaultMonthOption(""); setSelectedMonth(e.target.value) }} />

                <CustomDropdown
                  position="bottom-right"
                  className="myDropdown"
                >
                  <CustomDropdownButton arrow={false} className="btnClass">
                    <h3 className="!text-[16px] px-5 py-1 capitalize flex items-center gap-1">
                      <Calendar className="!w-4 !h-4" />
                      {defaultMonthOption ? defaultMonthOption?.replaceAll("_", " ") : moment(selectedMonth, "YYYY-MM").format("MMMM YYYY")}
                    </h3>


                  </CustomDropdownButton>

                  <CustomDropdownMenu className="w-full">

                    <CustomDropdownMenuList onClick={() => { setDefaultMonthOption("this_month") }}>
                      <div
                        className={
                          "d-flex whitespace-normal align-items-center gap-1"
                        }
                      >
                        This Month
                      </div>
                    </CustomDropdownMenuList>

                    <CustomDropdownMenuList onClick={() => { setDefaultMonthOption("last_month") }}>
                      <div
                        className={
                          "d-flex whitespace-normal align-items-center gap-1"
                        }
                      >
                        Last Month
                      </div>
                    </CustomDropdownMenuList>

                    <CustomDropdownMenuList onClick={() => { setDefaultMonthOption("this_year") }}>
                      <div
                        className={
                          "d-flex whitespace-normal align-items-center gap-1"
                        }
                      >
                        This Year
                      </div>
                    </CustomDropdownMenuList>

                    <CustomDropdownMenuList onClick={() => { setDefaultMonthOption("last_year") }}>
                      <div
                        className={
                          "d-flex whitespace-normal align-items-center gap-1"
                        }
                      >
                        Last Year
                      </div>
                    </CustomDropdownMenuList>

                    <CustomDropdownMenuList onClick={openMonthPicker}>
                      <div
                        className={
                          "d-flex whitespace-normal align-items-center gap-1"
                        }
                      >
                        Custom Month
                      </div>
                    </CustomDropdownMenuList>
                  </CustomDropdownMenu>
                </CustomDropdown>
              </div>
            </FeildWraper>
          </div>
        </div>

        <div className="grid grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 gap-5 w-full pt-[20px]">
          <StatCardNew
            type="main"
            svg={
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M3.32352 13.0113C3.6739 10.009 4.18586 7.75784 4.66063 6.15851C5.04994 4.84711 5.24459 4.19141 6.04283 3.5957C6.84107 3 7.65697 3 9.28876 3H14.7113C16.3431 3 17.159 3 17.9572 3.5957C18.7554 4.19141 18.9501 4.84711 19.3394 6.15851C19.8142 7.75784 20.3261 10.009 20.6765 13.0113C21.0895 16.5497 21.2959 18.3189 20.1027 19.6594C18.9095 21 16.9758 21 13.1084 21H10.8916C7.02422 21 5.09052 21 3.89731 19.6594C2.70411 18.3189 2.91058 16.5497 3.32352 13.0113Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M9 7C9 8.65685 10.3431 10 12 10C13.6569 10 15 8.65685 15 7" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            }
            iconBgColor="#84CC16"
            heading={"Total Fills"}
            trend={"down"}
            value={
              kpiData?.total_fills
            }
            percentage={`${kpiData?.month || "All-time"} Entries`}
          />

          <StatCardNew
            svg={
              <svg xmlns="http://www.w3.org/2000/svg" width="44" height="47" viewBox="0 0 44 47" fill="none">
                <path d="M26.5026 25.8447C25.5974 29.3604 23.3282 31.2958 19.6951 31.6628" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M34.5716 36.0601H29.5866C28.1563 36.0601 26.9976 37.1964 26.9976 38.5992C26.9976 40.0019 28.1563 41.1383 29.5866 41.1383H31.1618" stroke="black" stroke-width="1.53" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M31.1676 41.1323H29.5925C28.1622 41.1323 27.0034 42.2687 27.0034 43.6714C27.0034 45.0742 28.1622 46.2106 29.5925 46.2106H31.1676" stroke="black" stroke-width="1.53" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M7.68508 25.0928H1.93967C1.29639 25.0928 0.774902 25.6042 0.774902 26.2351V42.6476C0.774902 43.2785 1.29639 43.7899 1.93967 43.7899H7.68508C8.32836 43.7899 8.84985 43.2785 8.84985 42.6476V26.2351C8.84985 25.6042 8.32836 25.0928 7.68508 25.0928Z" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M22.0848 9.26025L15.3135 12.1486L19.3087 21.6125" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M37.2451 9.91748L42.8457 12.3797L38.8686 21.4944" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                <foreignObject x="13.8814" y="-7.50069" width="32.2731" height="41.6249"><div xmlns="http://www.w3.org/1999/xhtml" style={{
                  backdropFilter: "blur(1.25px)",
                  clipPath: "url(#bgblur_0_7338_9104_clip_path)",
                  height: "100%",
                  width: "100%"
                }}></div></foreignObject><g filter="url(#filter0_di_7338_9104)" data-figma-bg-blur-radius="2.50016">
                  <path d="M37.9873 0.666504L37.764 22.0685H30.3348L33.0807 25.957C32.3203 24.3294 31.2642 22.5775 29.9727 21.8258C29.9727 21.8258 26.3275 20.8078 22.0486 21.4884V0.666504H37.9873Z" fill="#239CEC" />
                  <path d="M37.7771 0.874512L37.5574 21.8599H30.4021C30.2956 21.7829 30.1883 21.7103 30.0779 21.646L30.0544 21.6323L30.0291 21.6255H30.0281V21.6245C30.0275 21.6244 30.0262 21.6248 30.0251 21.6245C30.0231 21.624 30.0201 21.6226 30.0164 21.6216C30.0088 21.6195 29.9974 21.6165 29.9832 21.6128C29.9544 21.6052 29.912 21.594 29.8572 21.5806C29.7469 21.5535 29.5854 21.5161 29.3787 21.4731C28.9649 21.3873 28.3694 21.2803 27.6394 21.1948C26.2346 21.0303 24.3275 20.9455 22.2566 21.2466V0.874512H37.7771Z" stroke="url(#paint0_linear_7338_9104)" stroke-width="0.416694" />
                </g>
                <path d="M34.2515 21.6128H39.6529C42.3144 23.0451 42.3204 24.9924 40.4797 27.2947C42.8515 29.8753 42.6101 32.2191 39.5745 34.3202C40.6487 37.759 39.1762 39.6707 36.2793 40.742C37.8907 43.4587 37.0639 45.1692 34.5895 46.2108H19.5983C18.7112 46.2108 17.999 45.9445 17.4981 45.4533C16.9972 44.9561 15.066 43.3995 14.3116 42.7307C13.5633 42.0559 12.6097 41.7422 11.6381 41.7422H8.84985V25.0693C8.84985 25.0693 11.3725 25.4599 12.8994 24.6136C13.9736 24.0158 14.7099 23.0984 19.8639 21.5359C20.6122 21.305 21.3606 21.1452 22.0848 21.0328C26.3637 20.3521 30.0089 21.3701 30.0089 21.3701C31.3004 22.1218 32.3565 23.8737 33.1169 25.5014C33.7989 26.9515 34.155 28.0523 34.324 28.514C34.7826 29.97 34.674 31.4615 33.4127 32.2191C32.1513 32.9767 30.9202 32.4736 29.5442 31.1774C28.8562 30.5263 25.911 27.5197 25.911 27.5197" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M26.919 15.4157C27.263 16.0609 27.9571 16.5048 28.7597 16.5048H31.1315C32.3566 16.5048 33.3524 15.5282 33.3524 14.3267C33.3524 13.1252 32.3566 12.1486 31.1315 12.1486H28.9106C27.6855 12.1486 26.6897 11.172 26.6897 9.97056C26.6897 8.76906 27.6855 7.79248 28.9106 7.79248H31.2824C32.079 7.79248 32.7731 8.23638 33.1231 8.88152" stroke="white" stroke-width="1.53" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M30.021 6.14697V7.78645" stroke="white" stroke-width="1.53" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M30.021 16.5049V18.1384" stroke="white" stroke-width="1.53" stroke-linecap="round" stroke-linejoin="round" />
                <defs>
                  <filter id="filter0_di_7338_9104" x="13.8814" y="-7.50069" width="32.2731" height="41.6249" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                    <feOffset dy="0.833387" />
                    <feGaussianBlur stdDeviation="0.750049" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_7338_9104" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_7338_9104" result="shape" />
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                    <feOffset dx="2.50016" dy="2.50016" />
                    <feGaussianBlur stdDeviation="5.54203" />
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.6 0" />
                    <feBlend mode="normal" in2="shape" result="effect2_innerShadow_7338_9104" />
                  </filter>
                  <clipPath id="bgblur_0_7338_9104_clip_path" transform="translate(-13.8814 7.50069)"><path d="M37.9873 0.666504L37.764 22.0685H30.3348L33.0807 25.957C32.3203 24.3294 31.2642 22.5775 29.9727 21.8258C29.9727 21.8258 26.3275 20.8078 22.0486 21.4884V0.666504H37.9873Z" />
                  </clipPath><linearGradient id="paint0_linear_7338_9104" x1="32.0899" y1="27.1066" x2="15.2826" y2="15.3413" gradientUnits="userSpaceOnUse">
                    <stop stop-color="white" stop-opacity="0" />
                    <stop offset="1" stop-color="white" />
                  </linearGradient>
                </defs>
              </svg>
            }
            heading="Total Fuel Volume"
            HeadingIcon={CostIcon}
            trend={"down"}
            value={
              kpiData?.total_liters
            }
            percentage={`${kpiData?.month || "Lifetime"} Consumption (Liters)`}
          />

          <StatCardNew
            type="main"
            svg={
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M2.5 7.5V13.5C2.5 17.2712 2.5 19.1569 3.67157 20.3284C4.84315 21.5 6.72876 21.5 10.5 21.5H13.5C17.2712 21.5 19.1569 21.5 20.3284 20.3284C21.5 19.1569 21.5 17.2712 21.5 13.5V7.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M3.86909 5.31461L2.5 7.5H21.5L20.2478 5.41303C19.3941 3.99021 18.9673 3.2788 18.2795 2.8894C17.5918 2.5 16.7621 2.5 15.1029 2.5H8.95371C7.32998 2.5 6.51812 2.5 5.84013 2.8753C5.16215 3.2506 4.73113 3.93861 3.86909 5.31461Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path opacity="0.4" d="M12 7.5V2.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M6 18H11M6 15H9" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            }
            iconBgColor="#0095FF"
            heading={"Total Fuel Cost"}
            trend={"down"}
            value={
              kpiData?.total_cost
            }
            percentage={`${kpiData?.month || "All-time"} Expenditure (Pkr)`}
          />

          <StatCardNew
            svg={
              <svg xmlns="http://www.w3.org/2000/svg" width="42" height="44" viewBox="0 0 42 44" fill="none">
                <g clip-path="url(#clip0_7488_2172)">
                  <path d="M7.68002 28.37H1.74002C1.2043 28.37 0.77002 28.8043 0.77002 29.34V40.93C0.77002 41.4657 1.2043 41.9 1.74002 41.9H7.68002C8.21574 41.9 8.65002 41.4657 8.65002 40.93V29.34C8.65002 28.8043 8.21574 28.37 7.68002 28.37Z" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M8.65002 31.02L12.63 30.1C14.33 29.71 16.1 29.77 17.76 30.28L27.94 33.42C28.94 33.73 29.46 34.82 29.08 35.79C28.76 36.6 27.89 37.07 27.04 36.88L16.35 34.57" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M8.65002 39.14L17.96 42.15C20.13 42.85 22.49 42.75 24.59 41.85L40.42 35.11C41.82 34.52 41.21 32.42 39.72 32.65L29.02 34.33" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                  <foreignObject x="14.6699" y="-2.50485" width="26.3203" height="32.565"><div xmlns="http://www.w3.org/1999/xhtml" style={{
                    backdropFilter: "blur(1.25px)",
                    clipPath: "url(#bgblur_1_7488_2172_clip_path)",
                    height: "100%",
                    width: "100%",
                  }}></div></foreignObject><g filter="url(#filter0_di_7488_2172)" data-figma-bg-blur-radius="2.50016">
                    <path d="M30.21 6.17602C30.21 6.17822 30.2118 6.18 30.214 6.18H30.2161C30.2185 6.18 30.2207 6.17856 30.2216 6.17635L32.11 1.75C32.51 0.800004 31.6 -0.169996 30.63 0.170004L29.52 0.560004C29.29 0.640004 29.04 0.620004 28.84 0.490004L28.2 0.110004C27.97 -0.0299957 27.69 -0.0399957 27.44 0.0700043L26.45 0.530004C26.26 0.620004 26.03 0.630004 25.83 0.560004L25.22 0.340004C24.27 -0.0099957 23.35 0.930004 23.72 1.88L25.3289 6.03706C25.3296 6.03884 25.3313 6.04 25.3332 6.04C25.3384 6.04 25.3398 6.04717 25.3349 6.04891C21.3227 7.47899 17.17 12.0302 17.17 16.9C17.17 22.79 21.94 27.56 27.83 27.56C33.72 27.56 38.49 22.79 38.49 16.9C38.49 12.1624 33.8846 7.7344 30.2155 6.17233C30.2129 6.17121 30.21 6.17317 30.21 6.17602Z" fill="#E470CD" />
                    <path d="M27.5236 0.260941C27.7165 0.176034 27.9248 0.186578 28.0919 0.288284L28.0939 0.289261L28.7335 0.669144V0.668167C28.9923 0.8333 29.307 0.854628 29.588 0.757034H29.589L30.6993 0.366409C31.4973 0.0870644 32.2475 0.884019 31.9181 1.66817L30.0548 6.03535C30.0329 6.06023 30.0165 6.09033 30.0079 6.12325L30.0021 6.17598L30.006 6.21895C30.0235 6.30427 30.0925 6.36948 30.1788 6.38399C31.9705 7.15682 33.9922 8.62479 35.5695 10.4807C37.1607 12.3531 38.2812 14.5977 38.2814 16.8996C38.2814 22.6745 33.6051 27.3517 27.8302 27.3518C22.0553 27.3518 17.3781 22.6745 17.3781 16.8996C17.3782 14.5238 18.3921 12.2131 19.9103 10.3147C21.4288 8.4159 23.4393 6.94586 25.4044 6.24532C25.5187 6.20457 25.5595 6.09054 25.5421 6.00313C25.5372 5.97847 25.5262 5.95452 25.5118 5.93184L23.9142 1.80489V1.80391C23.6091 1.01959 24.3683 0.24863 25.1476 0.535355L25.1495 0.536331L25.7589 0.756058L25.7609 0.757034C26.0104 0.844372 26.2965 0.831507 26.5372 0.717972L26.5382 0.718948L27.5236 0.260941ZM25.2628 6.23555C25.2569 6.23336 25.2518 6.22946 25.2462 6.22676C25.2519 6.22936 25.2569 6.23346 25.2628 6.23555ZM25.4357 5.86153C25.4412 5.86481 25.447 5.8675 25.4523 5.87129C25.447 5.86759 25.4414 5.86476 25.4357 5.86153Z" stroke="url(#paint0_linear_7488_2172)" stroke-width="0.416694" />
                  </g>
                  <path d="M24.4399 20.89L31.8199 13.52" stroke="black" stroke-width="1.55" stroke-miterlimit="10" stroke-linecap="round" />
                  <path d="M25.1601 15.86C26.0437 15.86 26.7601 15.1437 26.7601 14.26C26.7601 13.3763 26.0437 12.66 25.1601 12.66C24.2764 12.66 23.5601 13.3763 23.5601 14.26C23.5601 15.1437 24.2764 15.86 25.1601 15.86Z" stroke="black" stroke-width="1.55" stroke-miterlimit="10" stroke-linecap="round" />
                  <path d="M31.43 21.32C32.3136 21.32 33.03 20.6036 33.03 19.72C33.03 18.8363 32.3136 18.12 31.43 18.12C30.5463 18.12 29.83 18.8363 29.83 19.72C29.83 20.6036 30.5463 21.32 31.43 21.32Z" stroke="black" stroke-width="1.55" stroke-miterlimit="10" stroke-linecap="round" />
                </g>
                <defs>
                  <filter id="filter0_di_7488_2172" x="14.6699" y="-2.50485" width="26.3203" height="32.565" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                    <feOffset dy="0.833387" />
                    <feGaussianBlur stdDeviation="0.750049" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_7488_2172" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_7488_2172" result="shape" />
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                    <feOffset dx="2.50016" dy="2.50016" />
                    <feGaussianBlur stdDeviation="5.54203" />
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.6 0" />
                    <feBlend mode="normal" in2="shape" result="effect2_innerShadow_7488_2172" />
                  </filter>
                  <clipPath id="bgblur_1_7488_2172_clip_path" transform="translate(-14.6699 2.50485)"><path d="M30.21 6.17602C30.21 6.17822 30.2118 6.18 30.214 6.18H30.2161C30.2185 6.18 30.2207 6.17856 30.2216 6.17635L32.11 1.75C32.51 0.800004 31.6 -0.169996 30.63 0.170004L29.52 0.560004C29.29 0.640004 29.04 0.620004 28.84 0.490004L28.2 0.110004C27.97 -0.0299957 27.69 -0.0399957 27.44 0.0700043L26.45 0.530004C26.26 0.620004 26.03 0.630004 25.83 0.560004L25.22 0.340004C24.27 -0.0099957 23.35 0.930004 23.72 1.88L25.3289 6.03706C25.3296 6.03884 25.3313 6.04 25.3332 6.04C25.3384 6.04 25.3398 6.04717 25.3349 6.04891C21.3227 7.47899 17.17 12.0302 17.17 16.9C17.17 22.79 21.94 27.56 27.83 27.56C33.72 27.56 38.49 22.79 38.49 16.9C38.49 12.1624 33.8846 7.7344 30.2155 6.17233C30.2129 6.17121 30.21 6.17317 30.21 6.17602Z" />
                  </clipPath><linearGradient id="paint0_linear_7488_2172" x1="30.6016" y1="28.8129" x2="11.3281" y2="12.2551" gradientUnits="userSpaceOnUse">
                    <stop stop-color="white" stop-opacity="0" />
                    <stop offset="1" stop-color="white" />
                  </linearGradient>
                  <clipPath id="clip0_7488_2172">
                    <rect width="41.98" height="43.38" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            }
            heading="Average Fuel Cost"
            HeadingIcon={CostIcon}
            trend={"down"}
            value={
              kpiData?.avg_cost
            }
            percentage={`Cost Per Fill (${kpiData?.month || "All-time"})`}
          />

          <StatCardNew
            svg={
              <svg xmlns="http://www.w3.org/2000/svg" width="51" height="50" viewBox="0 0 51 50" fill="none">
                <path d="M5.97647 0.776947C8.08476 0.776947 9.79621 2.49975 9.79621 4.622V31.4104H4.85314C3.36554 31.4104 2.15674 30.1956 2.15674 28.6961V4.61996C2.15674 2.49975 3.86819 0.776947 5.97647 0.776947Z" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M34.6825 42.5605L31.9434 44.4082H31.6768L28.1664 42.077L24.5786 44.4082H24.312L20.8037 42.077L17.2139 44.4082H16.988L13.4776 42.077L9.88984 44.4082H9.78809V31.5538" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M5.97644 0.774994H34.3182C37.0248 0.774994 39.2165 2.98329 39.2165 5.70576V23.534" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M16.7825 26.2647H29.6703" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M16.7825 20.2647H29.6703" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M16.7825 14.2647H29.6703" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M16.7825 8.26474H29.6703" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M16.7825 32.0538H29.6703" stroke="black" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M14.5438 26.3941C15.0046 26.3941 15.3782 26.1703 15.3782 25.8941C15.3782 25.618 15.0046 25.3941 14.5438 25.3941C14.083 25.3941 13.7095 25.618 13.7095 25.8941C13.7095 26.1703 14.083 26.3941 14.5438 26.3941Z" fill="black" />
                <path d="M14.5438 20.3941C15.0046 20.3941 15.3782 20.1703 15.3782 19.8941C15.3782 19.618 15.0046 19.3941 14.5438 19.3941C14.083 19.3941 13.7095 19.618 13.7095 19.8941C13.7095 20.1703 14.083 20.3941 14.5438 20.3941Z" fill="black" />
                <path d="M14.5438 14.3941C15.0046 14.3941 15.3782 14.1703 15.3782 13.8941C15.3782 13.618 15.0046 13.3941 14.5438 13.3941C14.083 13.3941 13.7095 13.618 13.7095 13.8941C13.7095 14.1703 14.083 14.3941 14.5438 14.3941Z" fill="black" />
                <path d="M14.5438 8.39413C15.0046 8.39413 15.3782 8.17028 15.3782 7.89414C15.3782 7.61799 15.0046 7.39413 14.5438 7.39413C14.083 7.39413 13.7095 7.61799 13.7095 7.89414C13.7095 8.17028 14.083 8.39413 14.5438 8.39413Z" fill="black" />
                <path d="M14.5438 32.7951C15.0046 32.7951 15.3782 32.4191 15.3782 31.9552C15.3782 31.4914 15.0046 31.1153 14.5438 31.1153C14.083 31.1153 13.7095 31.4914 13.7095 31.9552C13.7095 32.4191 14.083 32.7951 14.5438 32.7951Z" fill="black" />
                <foreignObject x="22.5305" y="11.5374" width="35.5542" height="41.1154"><div xmlns="http://www.w3.org/1999/xhtml" style={{
                  backdropFilter: "blur(1.5px)",
                  clipPath: "url(#bgblur_0_7488_2148_clip_path)",
                  height: "100%",
                  width: "100%",
                }}></div></foreignObject><g filter="url(#filter0_d_7488_2148)" data-figma-bg-blur-radius="3.00236">
                  <path d="M48.0536 42.7471L48.2769 21.3451H32.3383V42.167C36.6171 41.4864 40.2 42.8451 40.2 42.8451L40.6244 42.7471H48.0536Z" fill="#FF7D28" />
                </g>
                <path d="M36.9442 35.0943C37.2882 35.7395 37.9822 36.1834 38.7849 36.1834H41.1567C42.3818 36.1834 43.3776 35.2068 43.3776 34.0053C43.3776 32.8038 42.3818 31.8272 41.1567 31.8272H38.9358C37.7106 31.8272 36.7148 30.8507 36.7148 29.6492C36.7148 28.4477 37.7106 27.4711 38.9358 27.4711H41.3075C42.1042 27.4711 42.7982 27.915 43.1482 28.5601" stroke="white" stroke-width="1.53" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M40.0461 25.8256V27.4651" stroke="white" stroke-width="1.53" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M40.0461 36.1835V37.817" stroke="white" stroke-width="1.53" stroke-linecap="round" stroke-linejoin="round" />
                <defs>
                  <filter id="filter0_d_7488_2148" x="22.5305" y="11.5374" width="35.5542" height="41.1154" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                    <feOffset dy="1.00079" />
                    <feGaussianBlur stdDeviation="0.900709" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_7488_2148" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_7488_2148" result="shape" />
                  </filter>
                  <clipPath id="bgblur_0_7488_2148_clip_path" transform="translate(-22.5305 -11.5374)"><path d="M48.0536 42.7471L48.2769 21.3451H32.3383V42.167C36.6171 41.4864 40.2 42.8451 40.2 42.8451L40.6244 42.7471H48.0536Z" />
                  </clipPath></defs>
              </svg>
            }
            heading="Mostly Usage"
            HeadingIcon={CostIcon}
            trend={"down"}
            value={
              kpiData?.most_usage?.total_cost ?? "PKR 0"
            }
            percentage={kpiData?.most_usage?.location_name}
          />

          <div className="bg-white border col-span-3 border-gray-200 rounded-lg p-5 flex-1 flex flex-col justify-between gap-5">
            <div className="space-y-5">
              <div className="flex items-center justify-between h-4">
                <h3 className="text-sm font-medium text-[#0e121b] flex items-center gap-[5px]">
                  <ReportIcon />
                  Monthly Trends
                  {/* {activeChart == "Miss Reasons" ? "Report" : "Platforms Managed"} */}
                </h3>
                <div className="flex items-center gap-3">
                  <CustomDropdown
                    position="bottom-right"
                    className="myDropdown"
                  >
                    <CustomDropdownButton arrow={true} className="btnClass">
                      <h3 className="!text-[14px] !text-[#0E121B]">{monthlyTrendChart}</h3>
                    </CustomDropdownButton>

                    <CustomDropdownMenu className="w-full">
                      {monthlyTrendChart != "Liters" && (
                        <CustomDropdownMenuList
                          onClick={() => {
                            setMonthlyTrendChart("Liters");
                          }}
                        >
                          <div
                            className={
                              "d-flex whitespace-normal align-items-center gap-1"
                            }
                          >
                            Liters
                          </div>
                        </CustomDropdownMenuList>
                      )}
                      {monthlyTrendChart != "Cost" && (
                        <CustomDropdownMenuList
                          onClick={() => {
                            setMonthlyTrendChart("Cost");
                          }}
                        >
                          <div
                            className={
                              "d-flex whitespace-normal align-items-center gap-1"
                            }
                          >
                            Cost
                          </div>
                        </CustomDropdownMenuList>
                      )}
                    </CustomDropdownMenu>
                  </CustomDropdown>
                  <button className="text-[#A4A7AE]">
                    <MoreVertical className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <p className="text-sm text-[#535862]">Overview reports of Today</p>

              <div className="">
                {isDashboardApiLoaded ?
                  // <HighchartsReact highcharts={Highcharts} options={monthlyChart} />
                  <AreaChart chartData={chartData} />
                  :
                  <div className="h-[400px]">
                    <InnerLoader />
                  </div>
                }
              </div>
            </div>
          </div>

          <div className="bg-white border col-span-2 border-gray-200 rounded-lg p-5 flex-1 flex flex-col justify-between gap-5">
            <div className="space-y-5">
              <div className="flex items-center justify-between h-4">
                <h3 className="text-sm font-medium text-[#0e121b] flex items-center gap-[5px]">
                  <ReportIcon />
                  Monthly Location Breakdown
                  {/* {activeChart == "Miss Reasons" ? "Report" : "Platforms Managed"} */}
                </h3>
                <div className="flex items-center gap-3">
                  <CustomDropdown
                    position="bottom-right"
                    className="myDropdown"
                  >
                    <CustomDropdownButton arrow={true} className="btnClass">
                      <h3 className="!text-[16px]">{activeChart}</h3>
                    </CustomDropdownButton>

                    <CustomDropdownMenu className="w-full">
                      {activeChart != "Cost" && (
                        <CustomDropdownMenuList
                          onClick={() => {
                            setActiveChart("Cost");
                          }}
                        >
                          <div
                            className={
                              "d-flex whitespace-normal align-items-center gap-1"
                            }
                          >
                            Cost
                          </div>
                        </CustomDropdownMenuList>
                      )}
                      {activeChart != "Liter" && (
                        <CustomDropdownMenuList
                          onClick={() => {
                            setActiveChart("Liter");
                          }}
                        >
                          <div
                            className={
                              "d-flex whitespace-normal align-items-center gap-1"
                            }
                          >
                            Liter
                          </div>
                        </CustomDropdownMenuList>
                      )}
                    </CustomDropdownMenu>
                  </CustomDropdown>
                  <button className="text-[#A4A7AE]">
                    <MoreVertical className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <p className="text-sm text-[#535862]">Overview reports of Today</p>

              <div className="">
                {isDashboardApiLoaded ?
                  // <HighchartsReact highcharts={Highcharts} options={monthlyChart} />
                  <PostsStatusDonut height={400}
                    data={secondChartData}
                  />
                  :
                  <div className="h-[400px]">
                    <InnerLoader />
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
